/**
 * Deposit and cancellation amount helpers for consumer online booking payment.
 *
 * online_deposit_type / cancellation_charge_type are ChargeType raw values
 * (FIXED=0, PERCENTAGE=1) serialised by the backend CharField as "0"/"1".
 * Comparing with === 0 incorrectly treats fixed amounts as percentage.
 */

type DepositStylistInfo = {
  is_online_deposit_allowed?: boolean;
  online_deposit_type?: string | number | null;
  online_deposit_amount?: string | number | null;
};

type CancellationStylistInfo = {
  is_cancellation_charge_active?: boolean;
  cancellation_charge_type?: string | number | null;
  cancellation_charge_amount?: string | number | null;
};

/**
 * Returns true when the ChargeType is fixed (0/"0" or legacy "F").
 */
export function isFixedChargeType(
  chargeType: string | number | null | undefined
): boolean {
  return chargeType === "F" || String(chargeType) === "0";
}

/**
 * Calculates the deposit to charge, capped at the service total.
 */
export function calculateDepositAmount(
  stylistInfo: DepositStylistInfo | null | undefined,
  serviceTotalAmount: number
): number {
  if (!stylistInfo?.is_online_deposit_allowed) {
    return 0;
  }

  const depositAmt = Number(stylistInfo.online_deposit_amount);
  if (!Number.isFinite(depositAmt) || depositAmt <= 0) {
    return 0;
  }

  if (!Number.isFinite(serviceTotalAmount) || serviceTotalAmount <= 0) {
    return 0;
  }

  if (isFixedChargeType(stylistInfo.online_deposit_type)) {
    return Math.min(depositAmt, serviceTotalAmount);
  }

  const percentageAmount = parseFloat(
    ((serviceTotalAmount * depositAmt) / 100).toFixed(2)
  );
  return Math.min(percentageAmount, serviceTotalAmount);
}

/**
 * Calculates the cancellation charge, capped at the service total.
 */
export function calculateCancellationAmount(
  stylistInfo: CancellationStylistInfo | null | undefined,
  serviceTotalAmount: number
): number {
  if (!stylistInfo?.is_cancellation_charge_active) {
    return 0;
  }

  const chargeAmt = Number(stylistInfo.cancellation_charge_amount);
  if (!Number.isFinite(chargeAmt) || chargeAmt <= 0) {
    return 0;
  }

  if (!Number.isFinite(serviceTotalAmount) || serviceTotalAmount <= 0) {
    return 0;
  }

  if (isFixedChargeType(stylistInfo.cancellation_charge_type)) {
    return Math.min(chargeAmt, serviceTotalAmount);
  }

  const percentageAmount = parseFloat(
    ((serviceTotalAmount * chargeAmt) / 100).toFixed(2)
  );
  return Math.min(percentageAmount, serviceTotalAmount);
}
